/* eslint-disable react/prop-types */
import clsx from "clsx";
import React, { FunctionComponent, memo, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useRoute } from "wouter";

import { useLanguage } from "../../i18n/useLanguage";
import {
  NationalVehicleResultsRoute,
  NationalVendorLandingRoute,
} from "../../navigation/NationalRoutes";
import { createPathFromEntries } from "../../navigation/createPathFromEntries";
import { getDealerBasePath } from "../../navigation/getDealerBasePath";
import { getNationalBasePath } from "../../navigation/getNationalBasePath";
import { useGetDealerGroupDealerList } from "../../queries/useGetDealerGroupDealerList";
import { useIsNationalSite } from "../../stores/hooks/useIsNationalSite";
import { useSearchForm } from "../../stores/hooks/useSearchForm";
import { PrimaryButton } from "../../ui/Button/Button";
import { useIsDealerGroup } from "../DealerGroup/useIsDealerGroup";
import {
  useDealershipsCriteria,
  useDealershipsDistance,
  useDealershipsMode,
} from "../Dealerships/DealershipForm";
import { DealerInput } from "./inputs/DealerInput";
import { MakeInput } from "./inputs/MakeInput";
import { ModelInput } from "./inputs/ModelInput";
import { PostalCodeInput } from "./inputs/PostalCodeInput";
import { ProductTypeInput } from "./inputs/ProductTypeInput";
import { TrimInput } from "./inputs/TrimInput";
import { YearInput } from "./inputs/YearInput";

const useBasePath = (): null | string => {
  const isNational = useIsNationalSite();
  const isDealerGroup = useIsDealerGroup();
  const { selectedDealer } = useGetDealerGroupDealerList();
  const [language] = useLanguage();
  const [{ productType, vehicleSubmodel }] = useSearchForm();

  if (isDealerGroup) {
    if (!selectedDealer) {
      return null;
    }
    return `/${selectedDealer.dealerUrlCode}/${language}/dealer/${selectedDealer.dealerId}`;
  }

  if (!vehicleSubmodel || !productType) {
    return null;
  }
  if (isNational) {
    return getNationalBasePath();
  }
  return getDealerBasePath();
};

const VehicleForm: FunctionComponent = () => {
  const { t } = useTranslation();
  const [
    {
      postalCode,
      productType,
      vehicleManufacturerName,
      vehicleModelClassId,
      vehicleModelName,
      vehicleSubmodel,
      vehicleSubmodelTiresizeId,
      vehicleYear,
    },
  ] = useSearchForm();
  const [, setLocation] = useLocation();
  const [shouldRenderPostCode] = useRoute(NationalVendorLandingRoute);
  const [isNationalResultsPage] = useRoute(NationalVehicleResultsRoute);
  const [criteria] = useDealershipsCriteria();
  const [mode] = useDealershipsMode();
  const [distance] = useDealershipsDistance();
  const basePath = useBasePath();
  const ref = useRef<HTMLFormElement>(null);

  const qs = [
    shouldRenderPostCode && postalCode && `q=${postalCode}&distance=300`,
    isNationalResultsPage && `q=${criteria}&distance=${distance}&mode=${mode}`,
    vehicleSubmodelTiresizeId && `size=${vehicleSubmodelTiresizeId}`,
  ]
    .filter(Boolean)
    .join("&");

  const isDealerGroup = useIsDealerGroup();

  useEffect(() => {
    const formNode = ref.current!;

    // NOTE: helps preventing excessive api calls caused by swr `revalidateOnFocus` policy
    // https://swr.vercel.app/docs/revalidation
    const preventFocus = (event: FocusEvent): void => {
      event.stopPropagation();
    };

    formNode.addEventListener("focus", preventFocus);

    return () => {
      formNode.removeEventListener("focus", preventFocus);
    };
  }, []);

  return (
    <form
      onSubmit={(event): void => {
        if (!basePath) {
          return;
        }
        event.preventDefault();
        if (
          vehicleSubmodel &&
          vehicleYear &&
          vehicleManufacturerName &&
          vehicleModelName &&
          productType
        ) {
          const nextPath = [
            createPathFromEntries(
              [
                ["product", productType],
                ["type", "vehicle-search"],
                ["year", vehicleYear],
                ["make", vehicleManufacturerName],
                ["category", String(vehicleModelClassId)],
                ["model", vehicleModelName],
                ["trim", vehicleSubmodel],
              ],
              basePath
            ),
            qs,
          ]
            .filter(Boolean)
            .join("?");

          if (isDealerGroup) {
            window.open(nextPath, "_blank");
          } else {
            setLocation(nextPath);
          }
        }
      }}
      className="grid sm:grid-cols-2 md:grid-cols-3 xl:flex gap-2"
      noValidate
      ref={ref}
    >
      {isDealerGroup && <DealerInput />}
      <YearInput />
      <MakeInput />
      <ModelInput />
      <TrimInput />
      <ProductTypeInput />
      {shouldRenderPostCode && <PostalCodeInput />}
      <PrimaryButton
        className={clsx(
          "shrink-0 shadow-lg",
          shouldRenderPostCode && "sm:col-span-2 md:col-span-3"
        )}
        aria-label="viewResults"
        disabled={!basePath}
        id="vehicle-search-submit"
        type="submit"
      >
        {t("searchForm.viewResults")}
      </PrimaryButton>
    </form>
  );
};

export const Vehicle = memo(VehicleForm);
